import { IFaireMoney } from "@faire/web-api/indigofair/data/IFaireMoney";
import { IImage } from "@faire/web-api/indigofair/data/IImage";
import { IMerchandisedCollectionV2Response } from "@faire/web-api/indigofair/merchandised_collections/IMerchandisedCollectionV2Response";
import { responsiveMediaQueries } from "@faire/web/slate/Theme/responsiveMediaQueries";
import { TypographyStyles } from "@faire/web/ui/Typography";
import React from "react";
import styled from "styled-components";

import { ILeanProductTileWithBrand } from "@faire/retailer/features/MarketplaceItems/MarketplaceProduct/LeanProtos/ILeanProductTile";
import { BrandTile } from "@faire/retailer/features/MerchandisedCollection/Components/BrandTile";
import { ProductTile } from "@faire/retailer/features/MerchandisedCollection/Components/ProductTile";
import { Title } from "@faire/retailer/features/MerchandisedCollection/Components/Title";
import { SaveToBoardProvider } from "@faire/retailer/features/RetailerBoards/Components/SaveToBoard";

const DATA_TEST_ID = "merchandised-collections-item-section";

export const ItemListSection: React.FC<ItemListSection.IProps> = ({
  className,
  section,
  type,
}) => {
  const productTilesWithBrand =
    section.response_of_product_collection_type?.product_tiles.map((tile) => {
      return {
        ...tile,
        brand:
          section.response_of_product_collection_type?.brands_by_token[
            tile.product?.brand_token ?? ""
          ],
      };
    }) ?? [];

  const ID = `${DATA_TEST_ID}-${section.token}`;

  return (
    <Wrapper
      className={className}
      role="list"
      aria-labelledby={ID}
      data-test-id={DATA_TEST_ID}
    >
      <StyledTitle id={ID}>{section.name}</StyledTitle>
      {type ===
      IMerchandisedCollectionV2Response.IMerchandisedCollectionV2.Type
        .PRODUCT ? (
        <SaveToBoardProvider
          value={{ trackOrigin: "merchandised-collections" }}
        >
          {productTilesWithBrand.map(
            (product_tile: ILeanProductTileWithBrand) => {
              return (
                <ProductTile
                  key={product_tile.product?.token}
                  role="listitem"
                  value={product_tile}
                />
              );
            }
          )}
        </SaveToBoardProvider>
      ) : null}
      {type ===
      IMerchandisedCollectionV2Response.IMerchandisedCollectionV2.Type.BRAND ? (
        <SaveToBoardProvider
          value={{ trackOrigin: "merchandised-collections" }}
        >
          {section.response_of_brand_collection_type?.brand_tiles.map(
            (brand_tile: ItemListSection.IBrandTile) => {
              return (
                <BrandTile
                  key={brand_tile.brand?.token}
                  brand={brand_tile.brand}
                />
              );
            }
          )}
        </SaveToBoardProvider>
      ) : null}
    </Wrapper>
  );
};

export namespace ItemListSection {
  export interface IProps {
    className?: string;
    section: IMerchandisedCollectionV2Response.ISection;
    type?: keyof typeof IMerchandisedCollectionV2Response.IMerchandisedCollectionV2.Type;
  }

  export interface ISection {
    token: string | undefined;
    name: string | undefined;
    product_tiles: ILeanProductTileWithBrand[];
    brand_tiles: IBrandTile[];
  }

  export interface IBrandTile {
    brand: IBrand | undefined;
  }

  export interface IBrand {
    token: string | undefined;
    name: string | undefined;
    minimum_order_amount: IFaireMoney | undefined;
    link: string | undefined;
    squared_image: IImage | undefined;
  }
}

const Wrapper = styled.section`
  display: grid;

  grid-template-columns: [col-start] minmax(0, 1fr) [col-end];
  grid-column-gap: 12px;
  grid-row-gap: 32px;

  ${responsiveMediaQueries.tabletAndAbove} {
    grid-template-columns: [col-start] repeat(3, minmax(0, 1fr)) [col-end];
    grid-column-gap: 15px;
    grid-row-gap: 32px;
  }

  @media (min-width: 1280px) {
    grid-template-columns: [col-start] repeat(4, minmax(0, 1fr)) [col-end];
    grid-column-gap: 12px;
    grid-row-gap: 24px;
  }
`;

const StyledTitle = styled(Title)`
  grid-column: col-start / col-end;

  ${responsiveMediaQueries.mobile} {
    ${TypographyStyles({ h4: true, serif: true })};
    line-height: 24px;
  }
`;
